import type { PaginationResponse } from './pagination';

export interface IAddress {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface IEvent {
  address: IAddress;
  date: string;
}

export interface IProduct {
  id: string;
  title: string;
  type: string;
  image: string;
  delivery_nutror: string;
  download_hash: string;
  event?: IEvent;
  allows_card: boolean;
  valid_to_refund_request: boolean;
}

export interface IProducer {
  email: string;
  name: string;
  phone: string;
  address: string;
  id: number;
  showProducerContactInformation: boolean;
}

export interface IOrder {
  status: string;
  tracker_code: string;
  delivery_date: string;
  expected_date: string;
}

export enum EPaymentStatus {
  EM_DIA = 1,
  AGUARDANDO_PAGAMENTO = 2,
  SUSPENSO = 3,
  CANCELADO = 4,
  ATRASADO = 7,
  FINALIZADO = 9,
  TRIAL = 10,
  INADIMPLENTE = 11,
}

export enum ShoppingPaymentStatusId {
  ABERTA = 1,
  EM_PROCESSAMENTO = 2,
  PAGA = 3,
  CANCELADA = 4,
  AGUARDANDO_DOCUMENTACAO = 5,
  AGUARDANDO_REEMBOLSO = 6,
  REEMBOLSADO = 7,
  EM_ANALISE = 8,
  DUPLICADA = 9,
  EXPIRADA = 10,
  EM_RECUPERACAO = 11,
  USO_INTERNO = 12,
  TRIAL = 13,
  DELETADO = 14,
  AGUARDANDO_PAGAMENTO = 15,
  RECUSADO = 16,
  INADIMPLENTE = 17,
  AGENDADA = 18,
  NEGOCIADA = 19,
  REEMBOLSO_PARCIAL = 55,
}

export interface IPaymentStatus {
  id: EPaymentStatus;
  name: string;
  alias: string;
}

// export interface ShoppingPaymentStatus {
//   id: ShoppingPaymentStatusId;
//   name: string;
//   alias: string;
// }

export enum ERequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REPROVED = 'reproved',
}

export enum ERequestType {
  REQUEST = 'REQUEST',
}

export type TRefundRequestStatus = 'approved';

export interface ICanRefund {
  can_refund: boolean;
  can_refund_reason: string;
}

export interface IRefundRequest {
  exist: boolean;
  invoice_id: string;
  status: ERequestStatus;
  justification: string;
  requested_at: Date;
  cli_cod: number;
  name: string;
  email: string;
  phone: number;
  facebook: string;
  skype: string;
  showProducerContactInformation: boolean;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  expired: boolean;
  refund_due_date: string;
}

export interface IHasUserBetaModule {
  data: boolean;
}

export interface IShoppingProduct {
  subscription_id?: number;
  product: IProduct;
  producer: IProducer;
  customer: ICustomer;
  payment_status: ShoppingPaymentStatusId;
  payment_method: PaymentMethod;
  order: IOrder;
  invoice_id: string;
  created_at: string;
  received_at: string;
  total_value: number;
  freight_value: number;
  card_number: string;
  unlimited_installment: boolean;
  currency_code: string;
}

export interface ShoppingDeliveriesData extends PaginationResponse {
  items: ShoppingDelivery[];
}
export interface ShoppingDeliveriesResponse extends PaginationResponse {
  data: ShoppingDeliveriesData;
}
export interface ShoppingDelivery {
  invoiceId: number;
  subscriptionId: null | number;
  totalValue: number;
  freightValue: number;
  currencyCode: string;
  status: ShoppingPaymentStatusId;
  statusName: string;
  statusAlias: string;
  paymentMethod: EPaymentMethod;
  paymentMethodName: string;
  cardNumber: string | null;
  paidAt: Date | null;
  createdAt: Date;
  customer: Omit<ICustomer, 'celphone' | 'business_name'> & {
    phone: string | null;
  };
  product: ProductDelivery;
  producer: {
    id: number;
    name: string;
    phone?: string;
    email?: string;
    address: string;
    showContactInfo?: boolean;
  };
  canBeRefunded: boolean;
  isUnlimitedInstallments: boolean;
}

export interface ProductDelivery {
  id: number;
  title: string;
  type: string;
  chargeType: string;
  allowsCard: boolean;
  image: string | null;
  downloadHash: string | null;
  nutrorDelivery: boolean;
  event: DeliveryEvent | null;
  physicalDelivery: {
    status?: string | null;
    deliveryDate?: Date | null;
    expectedDeliveryDate?: Date | null;
    trackingCode?: string | null;
  } | null;
}

export interface DeliveryEvent {
  startDate: Date;
  endDate: Date;
  address: IAddress & {
    zipCode: string | null;
    complement: string | null;
  };
}

export interface IFrequency {
  frequency: string;
  frequency_type: string;
  frequency_reference: number;
  total_cycles: string;
  number_of_charges: string;
}

export interface PaymentMethod {
  id: EPaymentMethod;
  name: string;
}

export enum EPaymentMethod {
  BOLETO = 1,
  VISA = 2,
  MASTERCARD = 3,
  CARTAO_SAQUE_BB = 4,
  CONTA_CORRENTE = 5,
  PONTOS_DO_SALDO = 6,
  CARTAO_SAQUE_INFINITO = 7,
  VISA_ELECTRON = 8,
  PAYPAL = 9,
  ELO = 10,
  DESCONHECIDO = 11,
  CARTEIRA_MOIP = 12,
  VISA_2 = 13,
  AMEX = 14,
  MASTERCARD_2 = 15,
  DINERS = 16,
  DEBITO_BANCO_DO_BRASIL = 17,
  DEBITO_BRADESCO = 18,
  DEBITO_ITAU = 19,
  BOLETO_2 = 20,
  HIPERCARD = 21,
  DEBITO_BANRISUL = 22,
  HIPER = 23,
  ELO_2 = 24,
  PAYPAL_INTERNACIONAL = 25,
  BOLETO_CEF = 26,
  MULTIPLOS_CARTOES = 27,
  DISCOVER = 28,
  UNIONPAY = 29,
  JCB = 30,
  SALDO_EDUZZ = 31,
  PIX = 32,
  PAGDIVIDIDO = 33,
  PAGAMENTO_COMBINADO = 37,
}

export interface ICustomer {
  id: number;
  name: string;
  business_name: string;
  email: string;
  celphone: string;
}

export interface IAffiliate {
  id: number;
  email?: any;
}

export interface IShoppingSubscription extends Omit<IShoppingProduct, 'payment_status'> {
  id: number;
  first_invoice_id: string;
  frequency_details: IFrequency;
  payment_charges: string;
  cycles: string;
  total_price: number;
  unit_price: number;
  amount: number;
  updated_at: string;
  initial_date: string;
  canceled_at: string;
  next_charge_date: string;
  payment_method: PaymentMethod;
  affiliate: IAffiliate;
  allows_change_plan?: boolean;
  changed_plan_at?: string;
  currency: string;
  showProducerContactInformation: boolean;
  payment_status: IPaymentStatus;
}

export interface ITicket {
  amount?: number;
  coupon_id?: number;
  coupon_key?: string;
  created_at?: string;
  description?: string;
  discount?: number;
  id?: number;
  quantity?: number;
  transaction_item_id?: number;
  unit_value?: number;
}

export interface IFiscalDocuments {
  ext?: string;
  id?: number;
  name?: string;
  status?: string;
  title?: string;
  url?: string;
}

export type SubscriptionProductChangePlan = {
  id: number;
  title: string;
  undetermined_charges: string;
  frequency: string;
  frequency_type: string;
  value: number;
  payment_method_id: number;
  payment_method_name: string;
  card_number: string;
  lastInvoice: {
    due_date: string;
    creation_date: string;
    value: number;
  };
  nextInvoice: {
    due_date: string;
    creation_date: string;
    value: number;
    max_installments: number;
  };
  planType: 'upgrade' | 'downgrade';
  allows_installments: boolean;
};

export interface IShoppingContactProductor {
  email: string;
  name: string;
  phone: number;
  showProducerContactInformation: boolean;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}
